import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { emailWithTldValidator } from 'src/app/validators/email-tld.validator';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { FormCompanyService } from 'src/app/layouts/main-v2/services/forms/form-company.service';
import { IError } from 'src/app/models/error.model';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';
import { generatePassword, copyToClipboard } from 'src/app/utils/password.utils';

@Component({
  selector: 'app-company-contact-v2',
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.sass']
})
export class ContactComponent implements OnInit, OnDestroy {
  formCompanyContact!: FormGroup;
  loading = false;
  isEditMode = false;
  companyId: string | null = '';
  passwordCopied = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly companyFormService: FormCompanyService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly store: Store<{ warningPopup: IError }>
  ) {}

  ngOnInit(): void {
    this.isEditMode = this.companyFormService.isEditMode();
    this.companyId = this.companyFormService.getCompanyId();
    this.buildForm();
    
    // Se for modo de edição, escuta quando os dados são carregados
    if (this.isEditMode) {
      const savedContact = this.companyFormService.getContact();
      if (savedContact) {
        this.populateForm();
      } else {
        this.companyFormService.isDataLoaded().subscribe(loaded => {
          if (loaded) {
            this.populateForm();
          }
        });
      }
    }
    
  }

  private buildForm(): void {
    const savedContact = this.companyFormService.getContact();

    this.formCompanyContact = this.fb.group({
      legalEmail: [savedContact?.legalEmail ?? '', [Validators.required, emailWithTldValidator()]],
      ownerEmail: [savedContact?.ownerEmail ?? '', this.isEditMode ? [] : [Validators.required, emailWithTldValidator()]],
      ownerName: [savedContact?.ownerName ?? '', this.isEditMode ? [] : [Validators.required]],
      phone: [savedContact?.phone ?? ''],
      password: [savedContact?.password ?? (this.isEditMode ? '' : generatePassword())]
    });
  } 

  private populateForm(): void {
    const savedContact = this.companyFormService.getContact();
    if (savedContact) {
      this.formCompanyContact.patchValue({
        legalEmail: savedContact.legalEmail ?? '',
        ownerEmail: savedContact.ownerEmail ?? '',
        ownerName: savedContact.ownerName ?? '',
        phone: savedContact.phone ?? ''
      });
    }
  }

  goBack(): void {
    // Salva antes de voltar
    this.companyFormService.setContact(this.formCompanyContact.value);
    this.router.navigate(['../info'], { relativeTo: this.route });
  }

  saveAndNext(): void {
    if (this.formCompanyContact.invalid) {
      this.store.dispatch(setWarningText("Preencha e valide todos os campos obrigatórios."));
      this.formCompanyContact.markAllAsTouched();
      return;
    }

    this.companyFormService.setContact(this.formCompanyContact.value);

    // Navega para o próximo step usando rotas permitidas
    const allowed = this.companyFormService.getAllowedRoutes();
    if (allowed.includes('settings')) {
      this.router.navigate(['../settings'], { relativeTo: this.route });
    } else if (allowed.length > 0) {
      const last = allowed[allowed.length - 1];
      this.router.navigate(['..', last], { relativeTo: this.route });
    }
  }

  regeneratePassword(): void {
    this.formCompanyContact.patchValue({ password: generatePassword() });
    this.passwordCopied = false;
  }

  async copyPassword(): Promise<void> {
    const password = this.formCompanyContact.get('password')?.value;
    if (password) {
      const success = await copyToClipboard(password);
      if (success) {
        this.passwordCopied = true;
        setTimeout(() => this.passwordCopied = false, 2000);
      }
    }
  }

  ngOnDestroy(): void {
    // Salva os dados automaticamente quando o componente for destruído
    this.companyFormService.setContact(this.formCompanyContact.value);
  }
}
